import React, { useState } from "react";
import SEO from "../../../components/SEO";
import { ExternalLink, RefreshCw, Star } from "lucide-react";
import { useReviews } from "../../../context/ReviewsContext";

const GOOGLE_REVIEWS_URL =
  "https://www.google.com/maps/place/einsured.ca+-+Life+Insurance+%7C+Super+Visa+Insurance/@43.7579458,-79.6924803,17z/data=!3m1!4b1!4m6!3m5!1s0x882b355459104fed:0x843a7824e45478c4!8m2!3d43.7579458!4d-79.6924803!16s%2Fg%2F11h70rsb9c?entry=ttu&g_ep=EgoyMDI1MTIwOS4wIKXMDSoASAFQAw%3D%3D";

function Stars({ value = 0 }) {
  const full = Math.round(value);
  return (
    <div className="flex items-center gap-1">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={`h-4 w-4 ${i < full ? "text-yellow-400" : "text-gray-300"}`}
          fill={i < full ? "currentColor" : "none"}
          aria-hidden="true"
        />
      ))}
      <span className="ml-2 text-sm text-gray-600">{value.toFixed(1)}</span>
    </div>
  );
}

function Avatar({ src, name }) {
  const [ok, setOk] = useState(true);
  const initial = (name?.trim?.()?.[0] || "A").toUpperCase();

  if (!src || !ok) {
    return (
      <div className="h-full w-full flex items-center justify-center font-bold text-gray-500 bg-gray-100">
        {initial}
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={name}
      className="h-full w-full object-cover"
      loading="lazy"
      referrerPolicy="no-referrer"
      onError={() => setOk(false)}
    />
  );
}

export default function Reviews() {
  const {
    reviews: rows,
    loading,
    loadingMore,
    error,
    averageRating: avg,
    refresh,
    loadMore,
    hasMore,
    total,
  } = useReviews();

  const formatDate = (dateValue) => {
    if (!dateValue) return "";

    if (dateValue instanceof Date) {
      return dateValue.toLocaleDateString();
    }

    const parsedDate = new Date(dateValue);

    return isNaN(parsedDate.getTime()) ? "" : parsedDate.toLocaleDateString();
  };

  return (
    <div className="bg-white">
      <SEO
        title="Avis clients et témoignages - einsured.ca"
        description="Découvrez les avis clients d’einsured.ca sur nos conseils d’assurance, notre service et notre soutien personnalisé partout au Canada."
      />

      <section className="bg-[#0b2034] text-white py-14 px-4 sm:px-6 md:px-24 text-center">
        <div className="max-w-5xl mx-auto">
          <p className="text-sky-200 font-bold tracking-wide text-sm">
            TÉMOIGNAGES
          </p>
          <h1 className="text-3xl sm:text-4xl font-bold mt-2">
            Ce que disent nos clients
          </h1>
          <p className="mt-3 text-sky-100 max-w-3xl mx-auto">
            Lisez les vrais commentaires de nos clients. Vous pouvez également consulter notre profil complet sur Google.
          </p>

          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
            <div className="bg-white/10 border border-white/10 rounded-2xl px-5 py-3">
              <Stars value={avg || 0} />
              <div className="mt-1 text-sm text-sky-100">
                {total || 0} avis
              </div>
            </div>

            <a
              href={GOOGLE_REVIEWS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-sky-500 hover:bg-sky-600 px-6 py-3 rounded-full font-semibold inline-flex items-center gap-2 transition-colors w-full sm:w-auto justify-center"
            >
              Voir sur Google <ExternalLink className="h-5 w-5" />
            </a>
            {/*
              <button
                onClick={refresh}
                disabled={loading}
                className="border border-white/20 hover:bg-white/10 px-6 py-3 rounded-full font-semibold inline-flex items-center gap-2 transition-colors disabled:opacity-50 w-full sm:w-auto justify-center"
              >
                <RefreshCw
                  className={`h-5 w-5 ${loading ? "animate-spin" : ""}`}
                />
                Actualiser
              </button>

              */}


          </div>
        </div>
      </section>

      <section className="bg-gray-50 py-12 px-4 sm:px-6 md:px-24">
        <div className="max-w-6xl mx-auto">
          {loading && rows.length === 0 && (
            <div className="text-center py-20 text-gray-600 font-medium">
              Chargement des avis...
            </div>
          )}

          {error && rows.length === 0 && (
            <div className="text-center text-red-500 py-20 bg-red-50 rounded-2xl border border-red-100">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {rows?.map((r) => (
              <article
                key={r.id}
                className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm hover:shadow-md transition-all flex flex-col"
              >
                <div className="flex items-center gap-3">
                  <div className="h-11 w-11 rounded-full overflow-hidden border bg-gray-100 flex-shrink-0">
                    <Avatar src={r.avatar} name={r.author} />
                  </div>
                  <div className="min-w-0">
                    <div className="font-bold text-gray-900 truncate">
                      {r.author}
                    </div>
                    <div className="text-xs text-gray-500">
                      {formatDate(r.date)}
                    </div>
                  </div>
                </div>

                <div className="mt-3">
                  <Stars value={r.rating || 0} />
                </div>

                <p className="mt-4 text-sm text-gray-700 leading-relaxed whitespace-pre-line flex-grow">
                  {r.text || "—"}
                </p>
              </article>
            ))}
          </div>

          {!loading && rows.length === 0 && !error && (
            <div className="text-center py-20 text-gray-500 bg-white rounded-2xl border border-dashed">
              Aucun avis trouvé pour le moment.
            </div>
          )}

          {rows.length > 0 && hasMore && (
            <div className="mt-10 flex justify-center">
              <button
                onClick={loadMore}
                disabled={loadingMore}
                className="bg-[#00B3FF] hover:bg-[#00A0E5] text-white font-semibold px-10 py-3 rounded-md shadow-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loadingMore ? "Chargement..." : "Charger plus"}
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
